/**
 * The things a person left behind, drawn rather than modelled.
 *
 * Graffiti, tallies and scratched arrows are writing, and writing is a hand:
 * the letters lean, the marker runs dry halfway through a word, the twentieth
 * day is scratched worse than the first. None of that survives being built out
 * of boxes, so these are painted on a canvas and hung on the wall as a single
 * two-triangle plane.
 *
 * Everything of one sort shares one texture. Phrases are laid out as rows of an
 * atlas and each variant gets its own tiny plane with UVs pointing at its row,
 * so twenty-six different scrawls cost one upload and one material — which is
 * why the answer to "the writing repeats" is more rows rather than more props.
 *
 * The hand itself — the pen, the scratch, the line somebody put through what
 * they had written — lives in `propdreadmarks.ts`. This file decides what the
 * marks say; that one decides what making a mark looks like.
 *
 * The register is the one the notes in `survival/notes.ts` use: short, first
 * person, and written by somebody who expected to need it later. Nothing here
 * is a joke, and nothing explains anything.
 */

import * as THREE from 'three';
import { clamp01, TAU } from '../core/math';
import { makeRNG } from '../core/rng';
import type { RNG } from '../core/types';
import { dragOff, rgba, scratch, scrawl, strikeOut } from './propdreadmarks';

/** One row of an atlas: where it is, and how wide the drawn content came out. */
export interface AtlasEntry {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  /** width / height of the sampled rectangle. Drives the plane's proportions. */
  aspect: number;
}

export interface DecalAtlas {
  texture: THREE.Texture;
  entries: readonly AtlasEntry[];
  dispose(): void;
}

/** Texels trimmed off each row so mip levels never bleed one row into the next. */
const INSET = 1;

/**
 * Paint `count` rows onto one canvas and hand back the UV rectangle of each.
 * `paint` draws in row-local coordinates and returns the width it used.
 */
function buildAtlas(
  width: number,
  rowHeight: number,
  count: number,
  seed: number,
  paint: (ctx: CanvasRenderingContext2D, index: number, w: number, h: number, rng: RNG) => number
): DecalAtlas {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = rowHeight * count;
  const ctx = canvas.getContext('2d')!;
  const rng = makeRNG(seed);
  const entries: AtlasEntry[] = [];

  for (let i = 0; i < count; i++) {
    ctx.save();
    ctx.translate(0, i * rowHeight);
    ctx.beginPath();
    ctx.rect(0, 0, width, rowHeight);
    ctx.clip();
    const used = Math.max(8, Math.min(width, paint(ctx, i, width, rowHeight, rng)));
    ctx.restore();

    const top = i * rowHeight + INSET;
    const bottom = (i + 1) * rowHeight - INSET;
    entries.push({
      u0: INSET / width,
      u1: (used - INSET) / width,
      // Canvas y runs down; texture v runs up, and three flips the upload.
      v0: 1 - bottom / canvas.height,
      v1: 1 - top / canvas.height,
      aspect: (used - 2 * INSET) / (rowHeight - 2 * INSET),
    });
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  // Wall marks are almost always seen at a grazing angle; without this they
  // smear into nothing the moment you are not standing square to them.
  texture.anisotropy = 4;
  return {
    texture,
    entries,
    dispose(): void {
      texture.dispose();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Graffiti
 * ------------------------------------------------------------------ */

/**
 * What happened to a piece of writing after it was written.
 *
 * The phrase is only half of a mark. A sentence somebody wrote is a person; a
 * sentence somebody wrote and then somebody else *did something to* is two
 * people and a gap in between, and that gap is where all of the unease is. None
 * of these announces itself — every one of them reads as ordinary writing at
 * five metres and as something else at one.
 */
type GraffitiHand =
  /** Written, then crossed out by somebody who had found out otherwise. */
  | 'struck'
  /** Written twice, offset, smaller the second time. Not the same hand. */
  | 'twice'
  /** The same thing over and over until it ran off the wall. */
  | 'again'
  /** Stopped mid-word, and the pen went down the wall. */
  | 'unfinished'
  /** Old. Nearly gone. Whoever wrote it was here a very long time ago. */
  | 'faint'
  /** A number added afterwards, by a hand that presses harder. */
  | 'counted';

interface GraffitiMark {
  text: string;
  hand?: GraffitiHand;
}

/**
 * Left for whoever came next. Every one of these is either a direction, a
 * warning, or somebody proving to themselves that they were still there.
 *
 * **The first `GRAFFITI_MOUTH_ROWS` rows are load-bearing.** `world/gen/
 * slides.ts` hangs one of them beside a slide mouth by asking for a row index
 * directly — nothing under `world/` may import the renderer, so the contract is
 * the row order and it is written down at both ends. They are the three a
 * person would have written *standing at the hole*, and they have to keep
 * working on an ordinary wall too, because the general placer draws from the
 * same atlas.
 *
 * Twenty-six of them rather than eight. Not because a level should carry more
 * writing — it should carry less, and the budgets in `world/gen/propTables.ts`
 * are where that is decided — but because eight over a run meant reading the
 * same wall four times, and a phrase you have already read is furniture.
 */
export const GRAFFITI_MARKS: readonly GraffitiMark[] = [
  // Rows 0-2: at the mouth. Nobody warns anybody here; they just say what they
  // did, which is worse.
  { text: 'I AM GOING IN' },
  { text: 'THEY WENT FIRST', hand: 'faint' },
  { text: 'I COULD HEAR MUSIC' },

  // Directions, and the people who left them.
  { text: 'KEEP LEFT' },
  { text: 'NOT THIS WAY' },
  { text: 'THE SAME ROOM' },
  { text: 'DO NOT SLEEP HERE' },
  { text: 'SHE WENT AHEAD' },
  { text: 'COUNT THE DOORS' },
  { text: 'STILL HERE', hand: 'again' },
  { text: 'GO BACK' },
  { text: 'LEFT AT THE STAIN' },
  { text: 'DO NOT WAIT FOR ME' },
  { text: 'NOBODY IS AHEAD' },

  // The two things the game will kill you for not knowing. A player who never
  // finds the note should still be able to find this.
  { text: 'STOP IF IT GROWLS' },
  { text: 'GET LOW AND WAIT' },

  // Second look.
  { text: 'THIS ROOM IS SAFE', hand: 'struck' },
  { text: 'DAY', hand: 'counted' },
  { text: 'MY NAME WAS', hand: 'unfinished' },
  { text: 'NOT MY HANDWRITING' },
  { text: 'I WROTE THIS', hand: 'twice' },
  { text: 'WE WERE THREE' },
  { text: 'IT SOUNDS LIKE ME' },
  { text: 'THAT IS MY COAT' },
  { text: 'IT IS NOT ALMONDS' },
  { text: 'THE LIGHT STAYS ON', hand: 'faint' },
];

/**
 * How many of the leading rows are reserved for the slide mouths. Mirrored by
 * `GRAFFITI_MOUTH_ROWS` in `world/gen/slides.ts`; changing one without the
 * other puts an unrelated phrase beside every way down.
 */
export const GRAFFITI_MOUTH_ROWS = 3;

export function makeGraffitiAtlas(ink: string): DecalAtlas {
  const ROW = 64;
  return buildAtlas(512, ROW, GRAFFITI_MARKS.length, 0x6a17d3, (ctx, i, w, h, rng) => {
    const mark = GRAFFITI_MARKS[i]!;
    const baseline = h - 20;
    // The row is the whole budget, and how wide "sans-serif" resolves is the
    // browser's business, not ours. Fit the phrase to the row rather than let
    // a wide font run off the end of it.
    let size = 34;
    ctx.font = `bold ${size}px sans-serif`;
    const room = w - 24;
    const measured = ctx.measureText(mark.text).width;
    if (measured > room) size = Math.max(14, size * (room / measured));

    if (mark.hand === 'again') {
      // Written until it ran out of wall. Smaller and tighter than an ordinary
      // scrawl, the way a hand doing something over and over gets, and the
      // repeats fade — so what reads at distance is a band of writing rather
      // than a sentence, and only up close is it the same three words.
      //
      // The width is taken from where the pen actually finished, not from where
      // the cursor got to: an over-run row comes out as a two-metre decal, and
      // a two-metre decal on a three-metre wall runs round the corner.
      const small = size * 0.55;
      ctx.font = `bold ${small}px sans-serif`;
      const step = ctx.measureText(`${mark.text} `).width;
      let x = 10;
      let end = x;
      for (let n = 0; n < 4 && x + step < w * 0.68; n++) {
        end = scrawl(ctx, mark.text, x, baseline, small, ink, rng, 1 - n * 0.16);
        x += step;
      }
      return Math.min(w, end + 8);
    }

    const fade = mark.hand === 'faint' ? 0.34 : 1;
    const end = scrawl(ctx, mark.text, 10, baseline, size, ink, rng, fade);

    switch (mark.hand) {
      case 'struck':
        strikeOut(ctx, 10, end, baseline - size * 0.3, ink, rng);
        break;
      case 'twice': {
        // Smaller, tighter, lower, and less certain. Somebody read this and
        // wrote it again underneath, which means they did not recognise it.
        ctx.save();
        ctx.translate(rng.range(14, 30), rng.range(6, 11));
        ctx.rotate(rng.range(-0.05, 0.05));
        scrawl(ctx, mark.text, 10, baseline, size * 0.62, ink, rng, 0.7);
        ctx.restore();
        break;
      }
      case 'unfinished':
        dragOff(ctx, end + 2, baseline, size, ink, rng);
        return Math.min(w, end + size * 1.2);
      case 'counted': {
        // The number is the other hand: bigger, leaning the other way, pressed
        // hard enough to have been meant.
        ctx.save();
        ctx.translate(end + size * 0.35, rng.range(-3, 3));
        ctx.rotate(rng.range(0.04, 0.13));
        const tail = scrawl(ctx, '40', 0, baseline, size * 1.25, ink, rng);
        ctx.restore();
        return Math.min(w, end + size * 0.35 + tail + 12);
      }
      default:
        break;
    }
    return end + 10;
  });
}

/* ------------------------------------------------------------------ *
 * Tally marks
 * ------------------------------------------------------------------ */

/**
 * How a count went wrong.
 *
 * A tally is already the best prop in the building — somebody sat here long
 * enough to lose track — and the arithmetic is where the rest of it lives. Each
 * of these is a real thing that happens to a person keeping count of days in a
 * place with no daylight, and none of them needs explaining to be read.
 */
type TallyFault =
  /** A gap, and then it begins again at one. They lost it and started over. */
  | 'restart'
  /** Small and neat for a fortnight, then large and wild. Not the same person. */
  | 'twohands'
  /** A group of nine with no bar across it. The rule went before the count did. */
  | 'overrun'
  /** The whole block struck through. Somebody decided it had all been wrong. */
  | 'struck';

interface TallyCount {
  total: number;
  fault?: TallyFault;
}

/**
 * Days, counted in fives. The counts climb and the hand gets worse: the jitter
 * grows with the mark index, so the last group of a long count is barely
 * legible. The final group is left incomplete, because it always is.
 */
const TALLY_COUNTS: readonly TallyCount[] = [
  { total: 4 },
  { total: 13 },
  { total: 26 },
  { total: 44 },
  { total: 31, fault: 'restart' },
  { total: 38, fault: 'twohands' },
  { total: 22, fault: 'overrun' },
  { total: 17, fault: 'struck' },
];

export function makeTallyAtlas(ink: string, lip: string): DecalAtlas {
  const ROW = 96;
  return buildAtlas(512, ROW, TALLY_COUNTS.length, 0x2b95f1, (ctx, i, w, h, rng) => {
    const { total, fault } = TALLY_COUNTS[i]!;
    const top = h * 0.22;
    const bottom = h * 0.86;
    const groupGap = 13;
    let x = 8;
    // Where the count restarted, so the gap is drawn once rather than every
    // time the group index happens to come back round.
    const breakAt = fault === 'restart' ? 19 : -1;
    // The group that ran long. Counted in marks, not groups, so it lands in the
    // middle of a count of any length.
    const overrunAt = fault === 'overrun' ? 10 : -1;
    let group = 0;

    for (let n = 0; n < total; n++) {
      // The longer this went on the worse it got.
      const wear = clamp01(n / 30);
      // Two hands: the first fortnight is somebody with a steady grip and a
      // small stroke, and everything after it is not.
      const small = fault === 'twohands' && n < 14;
      const shake = small ? 0.6 : 1 + wear * 3.4;
      const step = small ? 6.5 : 9;
      const height = small ? 0.66 : 1;
      const rowTop = bottom - (bottom - top) * height;

      if (n === breakAt) {
        // The gap. Nothing marks it, which is what makes it read as time rather
        // than as punctuation.
        x += 34;
        group = 0;
      }
      // The long group starts here and runs for nine before the counting
      // recovers. Both ends reset the group so the marks either side of it are
      // still grouped in fives — it is one bad stretch, not a new system.
      const overrun = fault === 'overrun' && n >= overrunAt && n < overrunAt + 9;
      if (fault === 'overrun' && (n === overrunAt || n === overrunAt + 9)) group = 0;

      const crossing = group === 4 && !overrun;
      if (crossing) {
        // The fifth mark is the one drawn across the other four.
        scratch(
          ctx,
          x - step * 4.4 + rng.range(-shake, shake),
          bottom + rng.range(-shake, shake),
          x + step * 0.3 + rng.range(-shake, shake),
          rowTop + rng.range(-shake, shake),
          3.2,
          ink,
          lip,
          rng
        );
        x += groupGap;
        group = 0;
      } else {
        scratch(
          ctx,
          x + rng.range(-shake, shake),
          rowTop + rng.range(-shake, shake),
          x + rng.range(-shake * 1.4, shake * 1.4),
          bottom + rng.range(-shake, shake),
          3.2,
          ink,
          lip,
          rng
        );
        x += step;
        group++;
      }
    }

    const used = Math.min(w, x + 8);
    if (fault === 'struck') {
      // Gouged across the lot in one go, hard enough to have taken the paper
      // with it. Scratched rather than drawn, because everything else here is.
      for (let s = 0; s < 2; s++) {
        scratch(ctx, 4, top + rng.range(-6, 6), used - 4, bottom + rng.range(-8, 4), 4.4, ink, lip, rng);
      }
    }
    return used;
  });
}

/* ------------------------------------------------------------------ *
 * Arrows
 * ------------------------------------------------------------------ */

/**
 * How many different hands the atlas draws each direction in. Two is enough that
 * a level's arrows are not obviously the same stencil.
 */
const ARROW_STYLES = 2;

/**
 * Shaft angle per atlas row, in canvas radians.
 *
 * **Row order is load-bearing.** `PropSpot.variant`'s low bit is the side the
 * arrow points, and `variantIndex` picks a row with `variant % rows` — so the
 * side bit only survives if it stays the low bit of the row index. Rows are
 * therefore left/right pairs: row `2 * style + side`. An odd row count, or a row
 * whose shaft is not horizontal, silently starts pointing arrows nowhere or the
 * wrong way, which is exactly what happened the first time this was built.
 *
 * Canvas +x is the prop's *left* by the time `wallDecal` has turned the quad
 * round to face the room, so side 0 is a shaft at angle 0 and side 1 at PI.
 */
export const ARROW_FORM_ANGLES: readonly number[] = Array.from(
  { length: ARROW_STYLES * 2 },
  (_, i) => ((i & 1) === 0 ? 0 : Math.PI)
);

/**
 * Scratched, never printed — a key or a belt buckle dragged over paint by
 * somebody who wanted the next person to get out.
 *
 * Nothing here is struck through or ambiguous. An arrow is the only direction
 * this game ever gives, and the placer has aimed every one of them at a real
 * exit; a crossed-out arrow would be noise in the one channel that carries
 * signal.
 */
export function makeArrowAtlas(ink: string, lip: string): DecalAtlas {
  const ROW = 96;
  const WIDTH = 176;

  return buildAtlas(256, ROW, ARROW_FORM_ANGLES.length, 0x40c2a9, (ctx, i, _w, h, rng) => {
    const angle = ARROW_FORM_ANGLES[i]!;
    // The second hand presses harder and re-traces more.
    const style = i >> 1;
    const passes = 3 + style;
    const weight = 3 + style * 0.6;

    const cx = WIDTH / 2;
    const cy = h / 2;
    // Half the shaft length. Driven by the row's *width*, because the shaft is
    // horizontal (see `ARROW_FORM_ANGLES`) — sizing it off `min(WIDTH, h)` let
    // the row height cap a mark that never used the height, so the arrow filled
    // under half its quad and vanished past about eight metres. The barbs are
    // what the height has to hold: they run back at 2.5 rad, so their vertical
    // reach is sin(2.5) * 0.6 * reach ~= 0.36 * reach, which at 0.41 * WIDTH
    // stays inside h/2 with room for the jitter `scratch` adds.
    const reach = WIDTH * (0.41 - style * 0.025);
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    const tipX = cx + dx * reach;
    const tipY = cy + dy * reach;
    const tailX = cx - dx * reach;
    const tailY = cy - dy * reach;

    // The shaft in a few passes with gaps, the way a key drags over paper.
    for (let s = 0; s < passes; s++) {
      const a = s / passes + rng.range(0, 0.05);
      const b = (s + 1) / passes - rng.range(0.02, 0.1);
      scratch(
        ctx,
        tailX + (tipX - tailX) * a,
        tailY + (tipY - tailY) * a,
        tailX + (tipX - tailX) * b,
        tailY + (tipY - tailY) * b,
        weight,
        ink,
        lip,
        rng
      );
    }
    for (const side of [-1, 1] as const) {
      const back = angle + side * 2.5;
      scratch(
        ctx,
        tipX,
        tipY,
        tipX + Math.cos(back) * reach * 0.6,
        tipY + Math.sin(back) * reach * 0.6,
        weight,
        ink,
        lip,
        rng
      );
    }
    return WIDTH;
  });
}

/* ------------------------------------------------------------------ *
 * Signs
 * ------------------------------------------------------------------ */

interface SignSpec {
  text: string;
  plate: number;
  ink: number;
  /** Bigger for the ones meant to be read from across a room. */
  size: number;
}

/**
 * The building's own voice. Printed, official, and — in the case of the second
 * one, which is a real sign that exists in real buildings — no longer funny.
 *
 * Rows 6 to 8 are the same trick as row 1 and are the reason the list grew.
 * Every one of them is a sign that is screwed to a wall somewhere in the real
 * world, unaltered: a lift notice, a library notice, an office notice. Nothing
 * has been made spooky. What makes them land is the building they are in — a
 * fire instruction is addressed to somebody who is expected to leave, a request
 * for quiet is addressed to somebody who is expected to be heard, and a desk
 * for the things people have lost is addressed to somebody who is expected to
 * come back for them.
 *
 * **Row order below index 0 is not free**: `SIGN_ROW` names the rows other
 * files ask for by number. Append; do not reorder.
 */
const SIGNS: readonly SignSpec[] = [
  { text: 'EXIT', plate: 0x2c6540, ink: 0xf1f4ec, size: 46 },
  { text: 'THIS IS NOT AN EXIT', plate: 0xe6e1d3, ink: 0x8c2a20, size: 26 },
  { text: 'NO ADMITTANCE', plate: 0xe6e1d3, ink: 0x2b2b28, size: 30 },
  { text: 'KEEP DOOR CLOSED', plate: 0xe6e1d3, ink: 0x2b2b28, size: 28 },
  { text: 'B-14', plate: 0xd4cfc0, ink: 0x2b2b28, size: 44 },
  { text: 'STAIRS', plate: 0xd4cfc0, ink: 0x2b2b28, size: 40 },
  { text: 'IN CASE OF FIRE DO NOT USE', plate: 0xe6e1d3, ink: 0x8c2a20, size: 22 },
  { text: 'QUIET PLEASE', plate: 0xd4cfc0, ink: 0x2b2b28, size: 34 },
  { text: 'LOST PROPERTY', plate: 0xd4cfc0, ink: 0x2b2b28, size: 30 },
];

/**
 * How many signs the atlas draws, and which row each of the useful ones is on.
 *
 * A placer that wants a *particular* sign — an EXIT beside the way down rather
 * than whatever the dice said — passes `PropSpot.variant = SIGN_COUNT * n + row`
 * for any whole `n`, because `variantIndex` picks the row with `variant % rows`.
 * Row 0 is the one that matters: `buildSign` lights it, the way a real exit sign
 * is lit, and it is the only sign in the building that is visible in the dark.
 */
export const SIGN_COUNT = SIGNS.length;
export const SIGN_ROW = { exit: 0, notExit: 1, stairs: 5 } as const;

/**
 * `tint` pulls each plate colour toward the level, so the same EXIT sign is
 * warm in the lobby and cold under the poolroom light.
 */
export function makeSignAtlas(tint: (hex: number) => string): DecalAtlas {
  const ROW = 96;
  return buildAtlas(512, ROW, SIGNS.length, 0x8f31b7, (ctx, i, w, h, rng) => {
    const sign = SIGNS[i]!;
    const plate = tint(sign.plate);
    const ink = tint(sign.ink);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let size = sign.size;
    ctx.font = `bold ${size}px sans-serif`;
    let width = Math.ceil(ctx.measureText(sign.text).width + size * 1.5);
    if (width > w) {
      // A wide font would run the plate off the row. Sign shops set smaller type.
      size = Math.max(10, (size * (w - 4)) / width);
      ctx.font = `bold ${size}px sans-serif`;
      width = Math.ceil(ctx.measureText(sign.text).width + size * 1.5);
    }

    ctx.fillStyle = plate;
    ctx.fillRect(0, 0, width, h);
    // A thin inner rule, the way plastic signage is always edged.
    ctx.strokeStyle = rgba(ink, 0.35);
    ctx.lineWidth = 2;
    ctx.strokeRect(5, 5, width - 10, h - 10);

    ctx.fillStyle = ink;
    ctx.fillText(sign.text, width / 2, h * 0.54);

    // Age it: a few dust streaks and one long scuff. Nothing down here is new.
    ctx.save();
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = '#000000';
    for (let s = 0; s < 6; s++) {
      const x = rng.range(0, width);
      ctx.fillRect(x, rng.range(0, h * 0.6), rng.range(1, 4), rng.range(h * 0.2, h));
    }
    ctx.globalAlpha = 0.25;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(rng.range(0, width * 0.4), rng.range(0, h));
    ctx.lineTo(rng.range(width * 0.6, width), rng.range(0, h));
    ctx.stroke();
    ctx.restore();

    return width;
  });
}

/* ------------------------------------------------------------------ *
 * Barrier tape
 * ------------------------------------------------------------------ */

/** The one row `makeTapeAtlas` draws. */
export const TAPE_STRIPES = 0;

/**
 * A length of hazard tape, for whoever strung it across a slide mouth.
 *
 * This used to be a two-row atlas that also carried a printed DOWN placard for
 * the chute the slide used to be. The placard is gone with it: a slide is not
 * a fitting the building installed and would not carry the building's printing.
 * The tape stays because it is not the building's — it is somebody's, put up by
 * a person who came through here, decided this was a thing that ought to be
 * stopped, and had one roll of tape to do it with.
 *
 * The stripes are drawn wide rather than tiled: a caller maps this row across a
 * face with `remapUV`, and a row with eight bands on it gives a 1.5 m run
 * roughly the stripe pitch a real one has without anybody having to set a
 * texture repeat per part.
 */
export function makeTapeAtlas(warn: string, dark: string): DecalAtlas {
  const ROW = 128;
  const WIDTH = 1024;
  /** Canvas pixels per warn+dark pair. Also the stripes' 45-degree run. */
  const PITCH = 128;

  return buildAtlas(WIDTH, ROW, 1, 0x5ad19c, (ctx, _i, w, h, rng) => {
    ctx.fillStyle = warn;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = dark;
    // Leaning bands, drawn past both ends so the clip leaves no half stripe.
    for (let x = -PITCH - h; x < w + PITCH; x += PITCH) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x + PITCH / 2, 0);
      ctx.lineTo(x + PITCH / 2 + h, h);
      ctx.lineTo(x + h, h);
      ctx.closePath();
      ctx.fill();
    }

    // Handled, and up for a long time. Without this it is a decal; with it, it
    // is tape somebody has already been past.
    ctx.save();
    ctx.globalAlpha = 0.16;
    ctx.fillStyle = '#000000';
    for (let s = 0; s < 12; s++) {
      ctx.fillRect(rng.range(0, w), rng.range(0, h * 0.7), rng.range(3, 34), rng.range(4, h));
    }
    ctx.globalAlpha = 0.3;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    for (let s = 0; s < 4; s++) {
      const y = rng.range(h * 0.2, h * 0.9);
      ctx.beginPath();
      ctx.moveTo(rng.range(0, w * 0.5), y);
      ctx.lineTo(rng.range(w * 0.5, w), y + rng.range(-6, 6));
      ctx.stroke();
    }
    ctx.restore();
    return w;
  });
}

/* ------------------------------------------------------------------ *
 * Clock faces
 * ------------------------------------------------------------------ */

/**
 * The dial of a school-issue wall clock: a minute track, twelve markers, four
 * numerals and the ghost of a brand nobody reads.
 *
 * Drawn rather than modelled for the same reason the graffiti is. A dial built
 * out of boxes needs sixty of them to read as a clock and still comes out as a
 * disc with lumps on it; on a canvas it is twelve strokes and it is a clock from
 * across the room. The hands are geometry, because where they have stopped is
 * the only thing about it the player will remember.
 */
export function makeClockFace(faceHex: string, inkHex: string): THREE.Texture {
  const SIZE = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;
  const c = SIZE / 2;
  const rng = makeRNG(0x3d81);

  ctx.fillStyle = faceHex;
  ctx.beginPath();
  ctx.arc(c, c, c, 0, TAU);
  ctx.fill();

  ctx.strokeStyle = inkHex;
  ctx.lineCap = 'butt';
  for (let i = 0; i < 60; i++) {
    const hour = i % 5 === 0;
    const a = (i / 60) * TAU - Math.PI / 2;
    const r0 = c * (hour ? 0.72 : 0.82);
    const r1 = c * 0.9;
    ctx.lineWidth = hour ? 7 : 2;
    ctx.beginPath();
    ctx.moveTo(c + Math.cos(a) * r0, c + Math.sin(a) * r0);
    ctx.lineTo(c + Math.cos(a) * r1, c + Math.sin(a) * r1);
    ctx.stroke();
  }

  ctx.fillStyle = inkHex;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${Math.round(SIZE * 0.15)}px sans-serif`;
  for (const [n, dx, dy] of [
    ['12', 0, -0.55],
    ['3', 0.55, 0],
    ['6', 0, 0.55],
    ['9', -0.55, 0],
  ] as const) {
    ctx.fillText(n, c + dx * c, c + dy * c + 2);
  }

  // The maker's name, printed small and long since faded out of legibility.
  ctx.globalAlpha = 0.3;
  ctx.font = `${Math.round(SIZE * 0.05)}px sans-serif`;
  ctx.fillText('QUARTZ', c, c * 1.42);
  ctx.globalAlpha = 1;

  // Dust settled on the inside of the glass, heaviest at the bottom.
  const dust = ctx.createLinearGradient(0, c * 0.7, 0, SIZE);
  dust.addColorStop(0, rgba('#2a2620', 0));
  dust.addColorStop(1, rgba('#2a2620', 0.22));
  ctx.fillStyle = dust;
  ctx.beginPath();
  ctx.arc(c, c, c, 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 0.09;
  ctx.fillStyle = '#000000';
  for (let i = 0; i < 14; i++) {
    ctx.beginPath();
    ctx.arc(rng.range(0, SIZE), rng.range(0, SIZE), rng.range(1, 9), 0, TAU);
    ctx.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/* ------------------------------------------------------------------ *
 * Stains and puddles
 * ------------------------------------------------------------------ */

/** Fade a square canvas to nothing at its edge so a decal has no visible border. */
function feather(ctx: CanvasRenderingContext2D, size: number): void {
  const mask = ctx.createRadialGradient(size / 2, size / 2, size * 0.2, size / 2, size / 2, size * 0.5);
  mask.addColorStop(0, 'rgba(255,255,255,1)');
  mask.addColorStop(0.82, 'rgba(255,255,255,1)');
  mask.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = mask;
  ctx.fillRect(0, 0, size, size);
  ctx.restore();
}

/**
 * Water damage: overlapping blooms with a concentrated rim, the shape a leak
 * makes after it has dried and gone again several times.
 */
export function makeStainTexture(tone: string, seed: number, strength = 0.55): THREE.Texture {
  const SIZE = 256;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;
  const rng = makeRNG(seed);

  for (let i = 0; i < 6; i++) {
    const a = rng.range(0, TAU);
    const r = rng.range(0, SIZE * 0.16);
    const bx = SIZE / 2 + Math.cos(a) * r;
    const by = SIZE / 2 + Math.sin(a) * r;
    const rad = rng.range(SIZE * 0.18, SIZE * 0.42);
    const g = ctx.createRadialGradient(bx, by, rad * 0.1, bx, by, rad);
    g.addColorStop(0, rgba(tone, strength * rng.range(0.55, 0.9)));
    g.addColorStop(0.6, rgba(tone, strength * 0.42));
    // The tide line: darker right at the edge, then gone.
    g.addColorStop(0.88, rgba(tone, strength * 0.6));
    g.addColorStop(1, rgba(tone, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, SIZE, SIZE);
  }

  feather(ctx, SIZE);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * Standing water. Darker than the floor and nearly opaque in the middle, with a
 * pale lip where the surface tension catches the torch.
 */
export function makePuddleTexture(tone: string, rim: string, seed: number): THREE.Texture {
  const SIZE = 256;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;
  const rng = makeRNG(seed);

  // One irregular body, built from a few overlapping discs so the outline is
  // never a circle.
  ctx.fillStyle = rgba(tone, 0.9);
  for (let i = 0; i < 5; i++) {
    const a = rng.range(0, TAU);
    const r = rng.range(0, SIZE * 0.14);
    ctx.beginPath();
    ctx.ellipse(
      SIZE / 2 + Math.cos(a) * r,
      SIZE / 2 + Math.sin(a) * r,
      rng.range(SIZE * 0.2, SIZE * 0.33),
      rng.range(SIZE * 0.18, SIZE * 0.3),
      rng.range(0, TAU),
      0,
      TAU
    );
    ctx.fill();
  }

  // Soften the boundary, then relight it.
  const edge = ctx.createRadialGradient(SIZE / 2, SIZE / 2, SIZE * 0.28, SIZE / 2, SIZE / 2, SIZE * 0.46);
  edge.addColorStop(0, rgba(rim, 0));
  edge.addColorStop(0.7, rgba(rim, 0.22));
  edge.addColorStop(1, rgba(rim, 0));
  ctx.save();
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillStyle = edge;
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.restore();

  feather(ctx, SIZE);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
